import { z } from 'zod';
export var ManagedAgentActionType;
(function (ManagedAgentActionType) {
    ManagedAgentActionType["FLAGGED_STALE"] = "flagged_stale";
    ManagedAgentActionType["SOFT_DELETED"] = "soft_deleted";
    ManagedAgentActionType["FLAGGED_BROKEN"] = "flagged_broken";
    ManagedAgentActionType["FLAGGED_SLOW"] = "flagged_slow";
    ManagedAgentActionType["FIXED_BROKEN"] = "fixed_broken";
    ManagedAgentActionType["CREATED_CONTENT"] = "created_content";
    ManagedAgentActionType["INSIGHT"] = "insight";
    // Autopilot attempted a mutation that an admin protection stopped
    ManagedAgentActionType["BLOCKED"] = "blocked";
})(ManagedAgentActionType || (ManagedAgentActionType = {}));
export var ManagedAgentTargetType;
(function (ManagedAgentTargetType) {
    ManagedAgentTargetType["CHART"] = "chart";
    ManagedAgentTargetType["DASHBOARD"] = "dashboard";
    ManagedAgentTargetType["SPACE"] = "space";
    ManagedAgentTargetType["PROJECT"] = "project";
})(ManagedAgentTargetType || (ManagedAgentTargetType = {}));
export var ManagedAgentScheduleOption;
(function (ManagedAgentScheduleOption) {
    ManagedAgentScheduleOption["EVERY_6_HOURS"] = "every_6_hours";
    ManagedAgentScheduleOption["EVERY_12_HOURS"] = "every_12_hours";
    ManagedAgentScheduleOption["DAILY"] = "daily";
    ManagedAgentScheduleOption["EVERY_2_DAYS"] = "every_2_days";
    ManagedAgentScheduleOption["WEEKLY"] = "weekly";
})(ManagedAgentScheduleOption || (ManagedAgentScheduleOption = {}));
export const ManagedAgentScheduleCronByOption = {
    [ManagedAgentScheduleOption.EVERY_6_HOURS]: '0 */6 * * *',
    [ManagedAgentScheduleOption.EVERY_12_HOURS]: '0 */12 * * *',
    [ManagedAgentScheduleOption.DAILY]: '0 0 * * *',
    [ManagedAgentScheduleOption.EVERY_2_DAYS]: '0 0 */2 * *',
    [ManagedAgentScheduleOption.WEEKLY]: '0 0 * * 0',
};
const LEGACY_HOURLY_CRON = '0 * * * *';
export const getManagedAgentScheduleCron = (schedule = ManagedAgentScheduleOption.DAILY) => ManagedAgentScheduleCronByOption[schedule];
export const getManagedAgentScheduleOption = (scheduleCron) => {
    if (scheduleCron === LEGACY_HOURLY_CRON) {
        return ManagedAgentScheduleOption.EVERY_6_HOURS;
    }
    const match = Object.entries(ManagedAgentScheduleCronByOption).find(([, cron]) => cron === scheduleCron);
    return match ? match[0] : ManagedAgentScheduleOption.DAILY;
};
export var ManagedAgentProtectedEntityType;
(function (ManagedAgentProtectedEntityType) {
    ManagedAgentProtectedEntityType["CHART"] = "chart";
    ManagedAgentProtectedEntityType["DASHBOARD"] = "dashboard";
    ManagedAgentProtectedEntityType["SPACE"] = "space";
})(ManagedAgentProtectedEntityType || (ManagedAgentProtectedEntityType = {}));
export const AGENT_SUGGESTIONS_SPACE_SLUG = 'agent-suggestions';
// Resolves which spaces Autopilot must not see. Selections inherit down the
// space tree; the Agent Suggestions space is always in scope so Autopilot can
// keep managing its own output.
export const computeAutopilotExcludedSpaceUuids = (spaces, mode, selectedSpaceUuids) => {
    const childrenByParent = new Map();
    spaces.forEach((space) => {
        if (space.parentSpaceUuid) {
            const children = childrenByParent.get(space.parentSpaceUuid) ?? [];
            children.push(space.uuid);
            childrenByParent.set(space.parentSpaceUuid, children);
        }
    });
    const withSubtrees = (rootUuids) => {
        const result = new Set();
        const queue = [...rootUuids];
        while (queue.length > 0) {
            const uuid = queue.pop();
            if (uuid !== undefined && !result.has(uuid)) {
                result.add(uuid);
                (childrenByParent.get(uuid) ?? []).forEach((child) => queue.push(child));
            }
        }
        return result;
    };
    const selected = withSubtrees(selectedSpaceUuids);
    const excluded = mode === 'all-except'
        ? selected
        : new Set(spaces
            .map((space) => space.uuid)
            .filter((uuid) => !selected.has(uuid)));
    spaces.forEach((space) => {
        if (space.slug === AGENT_SUGGESTIONS_SPACE_SLUG) {
            excluded.delete(space.uuid);
        }
    });
    return excluded;
};
// Per-field .catch() keeps one bad stored value from discarding the rest
const managedAgentPolicySchema = z.object({
    stalenessChartDays: z.number().int().min(7).max(3650).default(90).catch(90),
    stalenessDashboardDays: z
        .number()
        .int()
        .min(7)
        .max(3650)
        .default(90)
        .catch(90),
    previewProjectDays: z.number().int().min(7).max(3650).default(90).catch(90),
    slowQueryThresholdMs: z
        .number()
        .int()
        .min(100)
        .max(600000)
        .default(2000)
        .catch(2000),
    protectRecentDays: z.number().int().min(0).max(365).default(30).catch(30),
    escalationHours: z.number().int().min(0).max(720).default(24).catch(24),
    aggression: z
        .enum(['observe', 'flag', 'cleanup'])
        .default('cleanup')
        .catch('cleanup'),
    audience: z
        .enum(['admins', 'everyone'])
        .default('everyone')
        .catch('everyone'),
    spaceScopeMode: z
        .enum(['all-except', 'only'])
        .default('all-except')
        .catch('all-except'),
    verifiedContent: z
        .enum(['protected', 'none'])
        .default('protected')
        .catch('protected'),
});
export const DEFAULT_MANAGED_AGENT_POLICY = managedAgentPolicySchema.parse({});
export const resolveManagedAgentPolicy = (input) => {
    const result = managedAgentPolicySchema.safeParse(input ?? {});
    return result.success ? result.data : DEFAULT_MANAGED_AGENT_POLICY;
};
const REVERSIBLE_ACTION_TYPES = new Set([
    ManagedAgentActionType.SOFT_DELETED,
    ManagedAgentActionType.CREATED_CONTENT,
    ManagedAgentActionType.FIXED_BROKEN,
]);
export const getManagedAgentActionCategory = (actionType) => REVERSIBLE_ACTION_TYPES.has(actionType) ? 'undo' : 'dismiss';
export const getFixedBrokenMetadata = (metadata) => {
    const { previousVersionUuid } = metadata;
    return typeof previousVersionUuid === 'string'
        ? { previousVersionUuid }
        : null;
};
export var ManagedAgentRunStatus;
(function (ManagedAgentRunStatus) {
    ManagedAgentRunStatus["STARTED"] = "started";
    ManagedAgentRunStatus["COMPLETED"] = "completed";
    ManagedAgentRunStatus["ERROR"] = "error";
})(ManagedAgentRunStatus || (ManagedAgentRunStatus = {}));
//# sourceMappingURL=managedAgent.js.map